import type { AdminChatRoomResponse } from '@inflip/contracts/admin';
import { ChatRoomStatus, ChatRoomType, ORDER_STATUS_LABELS } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { AnimatedCheckbox } from '../components/animated-checkbox';
import { DataTable } from '../components/data-table';
import { Button, Chip, EmptyState, ErrorBanner, LoadingRow, PageHeader } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ORDER_STATUS_TEXT } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 20;

const COLUMNS: ColumnDef<AdminChatRoomResponse>[] = [
  {
    id: 'room',
    header: '방',
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        <Link
          to="/chats/$roomId"
          params={{ roomId: row.original.id }}
          className="font-mono text-xs font-medium text-brand hover:underline"
        >
          {row.original.id.slice(0, 8)}
        </Link>
        {row.original.status === ChatRoomStatus.CLOSED && (
          <span className="ml-1.5 text-xs font-bold text-muted-foreground">종료</span>
        )}
      </span>
    ),
  },
  {
    id: 'type',
    header: '유형',
    cell: ({ row }) => (
      <span
        className={`text-xs font-bold ${
          row.original.type === ChatRoomType.ORDER ? 'text-blue-600' : 'text-muted-foreground'
        }`}
      >
        {row.original.type === ChatRoomType.ORDER ? '거래' : '일반'}
      </span>
    ),
  },
  {
    id: 'orderStatus',
    header: '주문 상태',
    cell: ({ row }) =>
      row.original.orderStatus ? (
        <span className={`text-xs font-bold ${ORDER_STATUS_TEXT[row.original.orderStatus]}`}>
          {ORDER_STATUS_LABELS[row.original.orderStatus]}
        </span>
      ) : row.original.orderCount > 1 ? (
        // 그룹방 — 주문 상태가 갈릴 수 있어 건수로 표기 (상세에서 주문별 상태 확인)
        <span className="text-xs font-bold text-blue-600">{row.original.orderCount}건</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    id: 'listing',
    header: 'Listing',
    cell: ({ row }) => (
      <span className="block truncate text-xs">{row.original.listingTitle ?? '—'}</span>
    ),
  },
  {
    id: 'buyer',
    header: '구매자',
    cell: ({ row }) => row.original.buyerName ?? '—',
    meta: { tdClass: 'text-xs' },
  },
  {
    id: 'seller',
    header: '판매자',
    cell: ({ row }) => row.original.sellerName ?? '—',
    meta: { tdClass: 'text-xs' },
  },
  {
    id: 'messageCount',
    header: '메시지',
    cell: ({ row }) => row.original.messageCount,
    meta: { tdClass: 'text-xs tabular-nums' },
  },
  {
    id: 'lastMessage',
    header: '마지막 대화',
    cell: ({ row }) => (
      <>
        <div className="truncate">{row.original.lastMessagePreview ?? '—'}</div>
        {row.original.lastMessageAt && (
          <div className="text-[10px] text-muted-foreground">
            {formatDateTime(row.original.lastMessageAt)}
          </div>
        )}
      </>
    ),
    meta: { tdClass: 'text-xs' },
  },
  {
    id: 'admin',
    header: '어드민',
    cell: ({ row }) => (row.original.disputeAdminJoinedAt ? '참여 중' : '—'),
    meta: { tdClass: 'text-xs' },
  },
  {
    id: 'createdAt',
    header: '생성',
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
  },
];

export function ChatRoomsPage() {
  const { session } = useAdminAuth();
  // 목록 상태는 URL 에 — 상세에서 뒤로 나와도 보던 페이지·필터가 유지된다
  const [statusFilter, setStatusFilter] = useListViewState<'ALL' | ChatRoomStatus>('status', 'ALL');
  const [disputedOnly, setDisputedOnly] = useListViewState('disputedOnly', false);
  // 대시보드 "분쟁 끝난 방 미퇴장"에서 온다 — 분쟁이 끝났는데 운영자가 나가지 않은 방만
  const [adminStayed, setAdminStayed] = useListViewState('adminStayed', false);
  const [typeFilter, setTypeFilter] = useListViewState<'ALL' | ChatRoomType>('type', 'ALL');
  const [rooms, setRooms] = useState<AdminChatRoomResponse[]>([]);
  const [page, setPage] = useListViewState('page', 1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    api
      .listChatRooms(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(typeFilter === 'ALL' ? {} : { type: typeFilter }),
        ...(statusFilter === 'ALL' ? {} : { status: statusFilter }),
        ...(disputedOnly ? { disputedOnly: true } : {}),
        ...(adminStayed ? { adminStayed: true } : {}),
      })
      .then((rows) => !cancelled && setRooms(rows))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, statusFilter, disputedOnly, adminStayed, typeFilter, page]);

  if (!session) return null;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="채팅 관리"
        description="거래·일반 채팅방을 조회하고 필요 시 대화에 참여해요"
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {(['ALL', ...Object.values(ChatRoomStatus)] as const).map((s) => (
          <Chip
            key={s}
            active={statusFilter === s}
            onClick={() => {
              setPage(1);
              setStatusFilter(s);
            }}
          >
            {s === 'ALL' ? '전체' : s === ChatRoomStatus.OPEN ? '진행 중' : '종료'}
          </Chip>
        ))}
        <span className="mx-1 h-4 w-px bg-border" />
        {(['ALL', ...Object.values(ChatRoomType)] as const).map((t) => (
          <Chip
            key={t}
            active={typeFilter === t}
            onClick={() => {
              setPage(1);
              setTypeFilter(t);
            }}
          >
            {t === 'ALL' ? '유형 전체' : t === ChatRoomType.ORDER ? '거래' : '일반'}
          </Chip>
        ))}
        <label className="ml-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <AnimatedCheckbox
            checked={disputedOnly}
            onChange={(e) => {
              setPage(1);
              setDisputedOnly(e.target.checked);
            }}
          />
          어드민 참여 방만
        </label>
        <label className="ml-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <AnimatedCheckbox
            checked={adminStayed}
            onChange={(e) => {
              setPage(1);
              setAdminStayed(e.target.checked);
            }}
          />
          분쟁 끝났는데 나가지 않은 방만
        </label>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && <LoadingRow />}

      {!loading && rooms.length === 0 ? (
        <EmptyState message="채팅방이 없어요." />
      ) : (
        <DataTable data={rooms} columns={COLUMNS} getRowId={(r) => r.id} />
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          이전
        </Button>
        <span className="text-xs text-muted-foreground">{page} 페이지</span>
        <Button
          variant="outline"
          size="sm"
          disabled={rooms.length < PAGE_SIZE || loading}
          onClick={() => setPage((p) => p + 1)}
        >
          다음
        </Button>
      </div>
    </main>
  );
}
